import React, { useState, useRef } from 'react';
import { CaptureOverlay } from '../shared';
import type { VideoTake } from './useVideoRecorder';

interface VideoNotesProps {
  taskName: string;
  takes: VideoTake[];
  onSubmit: (notes: string) => void;
  onSaveDraft: (notes: string) => void;
  onBack?: () => void;
  onClose: () => void;
  initialNotes?: string;
}

const MAX_CHARS = 2000;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const NoteTakeCard: React.FC<{ take: VideoTake }> = ({ take }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className={`vc-video-card${take.orientation ? ` vc-video-card-${take.orientation}` : ''}`}>
      <video
        ref={videoRef}
        src={take.url}
        onEnded={() => setIsPlaying(false)}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <div className="vc-video-play" onClick={togglePlay}>
        {isPlaying ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </div>
      <div className="vc-video-dur">{formatTime(take.duration)}</div>
    </div>
  );
};

export const VideoNotes: React.FC<VideoNotesProps> = ({
  taskName,
  takes,
  onSubmit,
  onSaveDraft,
  onBack,
  onClose,
  initialNotes,
}) => {
  const [notes, setNotes] = useState(initialNotes || '');

  return (
    <CaptureOverlay taskName={taskName} onClose={onClose}>
      <div className="co-sheet">
        <div className="co-sheet-handle" />
        <div className="co-sheet-head">
          {onBack && (
            <button className="co-back-btn" onClick={onBack}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="15,18 9,12 15,6" />
              </svg>
            </button>
          )}
          <div className="co-sheet-title">Add a Note</div>
          <div className="co-sheet-badge">
            {takes.length} {takes.length === 1 ? 'take' : 'takes'}
          </div>
        </div>

        <div className="co-sheet-body">
          {/* Attached videos — scrollable take cards */}
          <div className="co-attached-label">Attached Videos</div>
          {takes.map(take => (
            <NoteTakeCard key={take.id} take={take} />
          ))}

          {/* Notes textarea */}
          <div className="co-note-wrap">
            <div className="co-note-label">Optional note</div>
            <textarea
              className="co-textarea"
              placeholder="Add context, reactions, or anything else…"
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value.slice(0, MAX_CHARS))}
            />
            <div className="co-charcount">{notes.length} / {MAX_CHARS}</div>
          </div>
        </div>

        <div className="co-sheet-actions">
          <button className="co-btn-ember" onClick={() => onSubmit(notes)}>
            Submit Task
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
          </button>
          <button className="co-btn-ghost" onClick={() => onSaveDraft(notes)}>
            Save Draft
          </button>
        </div>
      </div>
    </CaptureOverlay>
  );
};
